import {
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportPreviewRetrieveOptions,
  StepEnum
} from '@crm/api'
import { useImportLeads } from '@crm/features/leads/contexts'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { TableContainer, Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

export const ConfirmImport = () => {
  const { t } = useLocales()
  const { id } = useIdParam()
  const navigate = useAppNavigate()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { setIsPending } = useImportLeads()

  const [data, setData] = useState<string[][]>([])

  const { data: importPreview } = useQuery({
    ...leadsImportPreviewRetrieveOptions({ path: { id: id } })
  })

  const columns = useMemo(() => {
    const firstRow = importPreview?.data[0] || []

    return firstRow.map((item, key) => ({
      title: item.toString(),
      render: (row: string) => `${row[key]}`
    }))
  }, [importPreview])

  useEffect(() => {
    if (importPreview?.data?.length) {
      const [, ...rest] = importPreview.data
      setData(rest)
    }
  }, [importPreview])

  const { mutate: updateMutation } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      invalidateQueries(leadsImportListQueryKey())
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          response.step?.toString() || ''
        )
      )
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const onSubmit = () =>
    updateMutation({
      path: { id: id },
      body: {
        ...form.getFieldsValue(),
        step: StepEnum.CONFIRM_IMPORTED_FILE
      }
    })

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.description_for_confirm_import_step')}
      </Text>

      <Form
        onFinish={onSubmit}
        form={form}
        id={`form-${StepEnum.CONFIRM_IMPORTED_FILE}`}
        className="mt-20"
      >
        <TableContainer
          rowKey={() => uuidv4()}
          data={{ results: data, count: data?.length }}
          bordered
          showSearch={false}
          columns={columns}
          pagination={false}
          showSorterTooltip={false}
        />
      </Form>
    </>
  )
}
